// Pane-tree types — the pure data shapes of the recursive n-ary pane tree.
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition). This module
// carries ONLY type declarations + structural constants; every fold and helper
// lives in a sibling. See `../paneTree.ts` (the entry point) for the through-line.

import type { SearchFacet } from "../../Search/searchModel.js";
import type {
    ConversationId,
    FocusTarget,
    QualityTab,
    ResourceId,
    ResourceRef,
    ResourceTab,
} from "../types.js";

// === Tabs — the unified atom a pane holds (everything-is-a-tab) ==============
// Each kind is its own variant on `type`. The resource arm reuses the existing
// `ResourceTab` (carrying `dirty` as a property OF the tab, never a mode flag).
// A CONVERSATION tab covers BOTH the landing (empty, nascent) and an established
// conversation — the "hero" is derived from the session's transcript length, not
// a separate tab kind (hero-is-initial-conversation-state). Settings is the
// tree-wide singleton — at most one across ALL panes, enforced by the dedup in
// `openInFocused` / `moveTab`.
export type ConversationTab = {
    readonly type: "conversation";
    readonly id: ConversationId;
};
export type SettingsTab = { readonly type: "settings" };
// The Home overview tab — the Settings-sibling tree-wide singleton (dogfood F1).
export type HomeTab = { readonly type: "home" };
// The Studio canvas tab — the Home-sibling tree-wide singleton (Studio v0).
export type StudioTab = { readonly type: "studio" };
// The Projects list tab — the Home-sibling tree-wide singleton (#161).
export type ProjectsTab = { readonly type: "projects" };
// The Knowledge bank tab — the Home-sibling tree-wide singleton (#60). Like
// Search, its seed (the source gone into, where in it) lives on the MEMBER so
// a backgrounded tab keeps it; session-only (the URL carries the selection).
export type KnowledgeTab = {
    readonly type: "knowledge";
    readonly selectedId?: ResourceId;
    readonly target?: FocusTarget;
};
// The Search + Recent tab — the Home-sibling tree-wide singleton (dogfood F6).
// `kind` is the SEED facet the tab remembers (F48: "kind lives on the member,
// the active pointer just references it" — so `activeToKey`/`keyToActive` stay
// lossless, and re-activating a backgrounded Search tab keeps its facet). Absent
// ⇒ unfiltered. The live multi-select stays view-ephemeral in SearchSurface.
export type SearchTab = {
    readonly type: "search";
    readonly kind?: SearchFacet;
    // The seed pre-selected row (F88): a source-open routes to the library with
    // its row selected. Carried on the MEMBER (like `kind`) so re-activating a
    // backgrounded Search tab keeps its seed. Session-only — dropped on reload
    // by `serialize` (the seed only matters for the in-session open gesture).
    readonly selectedId?: ResourceId;
    // WHERE in the seed row's source to open (a chat tool's read locator);
    // session-only like `selectedId`.
    readonly target?: FocusTarget;
    // The live library query text; session-only (the URL `?q=` carries it).
    readonly query?: string;
};
export type Tab =
    | ResourceTab
    | ConversationTab
    | SettingsTab
    | HomeTab
    | StudioTab
    | ProjectsTab
    | KnowledgeTab
    | SearchTab
    // The full-quality report tab (types.ts `QualityTab`) — a per-material
    // COLLECTION member (keyed by `materialId`), not a tree-wide singleton.
    | QualityTab;

// A stable identity for a tab, so panes reference tabs and the active pointer
// never collides with a resource id (the ActiveTab lesson from `types.ts`,
// widened with the settings singleton).
export type TabKey =
    | { readonly type: "conversation"; readonly id: ConversationId }
    | { readonly type: "settings" }
    | { readonly type: "home" }
    | { readonly type: "studio" }
    | { readonly type: "projects" }
    | { readonly type: "knowledge" }
    | { readonly type: "search" }
    | { readonly type: "resource"; readonly id: ResourceId }
    | { readonly type: "quality"; readonly materialId: ResourceId };

// === Pane tree (n-ary, sizes as proportions) =================================
// PaneId is an opaque per-device label, `string` like ResourceId —
// minted by the shell, never constructed in the pure model (impl doc §3.2).
export type PaneId = string;

// A leaf: one pane = an ordered working set of tabs + which is active. This is
// the single-strip TabSet, minus the split-out conversation boolean.
//
// `preview` is the pane's ONE weak (glance) tab — VS Code's italic preview slot,
// relocated to the per-pane Leaf (nav-history doc §3a). A dedicated SLOT, not a
// `preview: boolean` on a Tab, makes two illegal states unrepresentable for
// free: (1) two previews cannot exist (one slot, not N flags), (2) a DIRTY
// preview cannot exist (the slot holds a bare `ResourceRef`, which has no
// `dirty` field — only `ResourceTab` does — so "editing must promote" is forced
// by the type, not remembered by a handler). The one fold-maintained invariant:
// `preview.id` never also appears in `tabs` (opening/editing MOVES it out of the
// slot into `tabs`, never duplicates). `active` may point at the preview's key.
export type Leaf = {
    readonly type: "leaf";
    readonly id: PaneId;
    readonly tabs: readonly Tab[]; // the KEPT working set, open order left→right
    readonly preview: ResourceRef | null; // the ONE weak (glance) tab, or none
    readonly active: TabKey; // always points at a member OR the preview (invariant)
};

// A branch: an orientation + an ORDERED list of children + their sizes.
// N-ary, not binary. sizes[i] ∈ (0,1], Σ = 1, len === children.len. A branch
// also carries a PaneId so the resize + focus folds address it uniformly.
export type Orientation = "row" | "column"; // row = side-by-side, column = stacked
export type Branch = {
    readonly type: "branch";
    readonly id: PaneId;
    readonly orientation: Orientation;
    readonly children: readonly Pane[]; // ≥ 2 (a 1-child branch collapses)
    readonly sizes: readonly number[]; // proportions, Σ = 1
};

export type Pane = Leaf | Branch;

// The workspace's centre. `focused` names the LEAF the keyboard + new-tab
// affordance target. A fresh workspace = a single Leaf holding the home tab
// (behaviourally identical to today's single strip — the migration base).
export type PaneTree = {
    readonly root: Pane;
    readonly focused: PaneId;
};

// Which edge of a pane a drag ends in — the split direction. From the pure
// `dropZone` hit-test (impl doc §4); the shell measures, this decides.
export type SplitEdge = "left" | "right" | "top" | "bottom";

// The minimum share a pane keeps when a sibling grows (VS Code clamps the
// same way). Proportion, not pixels.
export const MIN_PANE_FRACTION = 0.1;

// A restore-time depth cap so a pathological blob degrades to its top levels
// rather than blowing the stack (impl doc §R6, the scale-shaped-assumption
// guard). Human-authored trees never approach this.
export const MAX_PANE_DEPTH = 8;

// === Guarded-close outcomes (see `closeTabInPane`) ===========================
export type PaneTabClosed = {
    readonly type: "closed";
    readonly tree: PaneTree;
};
export type PaneCloseBlocked = {
    readonly type: "unsaved";
    readonly tab: ResourceTab;
};
export type PaneCloseOutcome = PaneTabClosed | PaneCloseBlocked;

// === Serialized form (see `serialize`) =======================================
export type SerializedPaneTree = {
    readonly root: unknown;
    readonly focused: PaneId;
};
